import { useState } from 'react';
import { alpha, useTheme } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ReactComponent as PlusIcon } from '../../../design-system/Icons/PlusIcon.svg';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import DSChip from '../../../design-system/DSChip';
import DSIconButton from '../../../design-system/DSIconButton';
import DSDraggableCard from '../../../design-system/DSDraggableCard';
import { PRIMARY_BUTTON_HOVER_COLOR } from '../consts';
import { useDragAndDrop } from '../../../hooks/useDragAndDrop';
import useAppContext from '../../../hooks/useAppContext';
import useCustomSections from '../hooks/useCustomSections';
import useArtistProfileTemplates from '../hooks/useArtistProfileTemplates';
import AddSectionDialog from './AddSectionDialog';
import SectionEditorCard from './SectionEditorCard';
import SectionTemplateDispatcher from './SectionTemplates';
import { getSectionTitle, ARTIST_PROFILE_BASE_URL } from '../consts';

type CustomSectionsMode = 'view' | 'edit';

const EmptySectionsPlaceholder = ({ onAction }: { onAction: () => void }) => {
  const { spacing, palette } = useTheme();
  return (
    <DSBox
      sx={{
        mt: spacing(3),
        borderRadius: spacing(3),
        border: `3px dashed ${palette.white[12]}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: spacing(8),
        gap: spacing(1),
      }}
    >
      <DSTypography
        sx={{
          fontFamily: 'Gilroy-Bold',
          fontSize: '16px',
          lineHeight: 1.3,
          letterSpacing: '0.15px',
          color: palette.common.white,
        }}
      >
        Your page is looking minimal.
      </DSTypography>
      <DSTypography variant="body2" color="text.secondary">
        Add videos, images or stories to give collectors more context.
      </DSTypography>
      <DSPrimaryButton
        sx={{
          mt: spacing(1.5),
          '&:hover': { backgroundColor: PRIMARY_BUTTON_HOVER_COLOR },
        }}
        onClick={onAction}
      >
        Add First Section
      </DSPrimaryButton>
    </DSBox>
  );
};

type CustomSectionsViewProps = {
  onBack: () => void;
};

const CustomSectionsView = ({ onBack }: CustomSectionsViewProps) => {
  const { spacing, palette } = useTheme();
  const { user, setUser } = useAppContext();
  const publishedSections = user?.artistProfile?.customSections ?? [];
  const hasPublishedSections = publishedSections.length > 0;
  const [mode, setMode] = useState<CustomSectionsMode>(
    hasPublishedSections ? 'view' : 'edit',
  );
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const { templates } = useArtistProfileTemplates();
  const {
    sections,
    setSections,
    addSection,
    removeSection,
    updateSectionData,
    publish,
    isPublishing,
    hasSections,
    hasActiveUploads,
    uploadCallbacks,
    resetFromPublished,
  } = useCustomSections(templates);

  const {
    displayItems,
    draggedIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    draggedItem,
  } = useDragAndDrop({
    items: sections,
    onOrderChange: setSections,
  });

  const handleSectionSelect = (sectionType: string) => {
    addSection(sectionType);
    setIsAddSectionOpen(false);
  };

  const handleEnterEditMode = () => {
    resetFromPublished();
    setMode('edit');
  };

  const handlePublish = () => {
    setUser((previousUser) =>
      previousUser
        ? {
            ...previousUser,
            artistProfile: {
              ...previousUser.artistProfile,
              customSections: sections,
              draftCustomSections: sections,
            },
          }
        : previousUser,
    );
    publish();
    setMode('view');
  };

  const containerSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    px: { xxs: spacing(2), sm: spacing(4) },
    pb: spacing(3),
    overflow: 'auto',
    borderRadius: spacing(3),
    background: `radial-gradient(ellipse at 0% 0%, ${alpha(palette.common.white, 0.1)} 0%, transparent 40%), radial-gradient(ellipse at 100% 0%, ${alpha(palette.common.white, 0.1)} 0%, transparent 40%)`,
    backgroundSize: '100% 600px',
    backgroundRepeat: 'no-repeat',
  };

  const previewButtonSx = {
    color: palette.common.white,
    borderColor: alpha(palette.common.white, 0.56),
    borderWidth: '1px',
    borderRadius: '1000px',
    px: { xxs: '20px', sm: '40px' },
    py: { xxs: '8px', sm: '14px' },
    fontFamily: 'Gilroy-Bold',
    fontSize: { xxs: '14px', sm: '16px' },
    lineHeight: '26px',
    letterSpacing: '0.46px',
    textTransform: 'capitalize' as const,
    '&:hover': {
      backgroundColor: alpha(palette.common.white, 0.1),
      color: palette.common.white,
      borderColor: alpha(palette.common.white, 0.56),
    },
  };

  const editButtonSx = {
    borderRadius: '1000px',
    px: { xxs: '20px', sm: '40px' },
    py: { xxs: '8px', sm: '14px' },
    fontFamily: 'Gilroy-Bold',
    fontSize: { xxs: '14px', sm: '16px' },
    lineHeight: '26px',
    letterSpacing: '0.46px',
    textTransform: 'capitalize' as const,
    '&:hover': {
      backgroundColor: PRIMARY_BUTTON_HOVER_COLOR,
    },
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  return (
    <>
      {/* ── VIEW MODE ── */}
      {isViewMode && (
        <DSBox sx={containerSx}>
          {/* Header */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xxs: 'flex-start', md: 'center' },
              gap: spacing(2),
              py: spacing(3),
            }}
          >
            <DSBox
              sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <DSTypography variant="h3" color="common.white">
                Custom Sections
              </DSTypography>
              <DSTypography variant="body1" color="text.secondary">
                Build your artist page by adding sections. These appear below
                your identity, collections, and Klydos on the marketplace.
              </DSTypography>
            </DSBox>

            <DSBox sx={{ display: 'flex', gap: '13px', flexShrink: 0 }}>
              <DSSecondaryButton
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                disabled={!user?.id}
                onClick={() => {
                  if (!user?.id) return;
                  window.open(
                    `${ARTIST_PROFILE_BASE_URL}?id=${user.id}`,
                    '_blank',
                  );
                }}
                sx={previewButtonSx}
              >
                Preview
              </DSSecondaryButton>
              {hasPublishedSections && (
                <DSPrimaryButton
                  onClick={handleEnterEditMode}
                  sx={editButtonSx}
                >
                  Edit
                </DSPrimaryButton>
              )}
            </DSBox>
          </DSBox>

          {/* Published Sections (read-only) */}
          {hasPublishedSections ? (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '40px',
                pt: '64px',
                pb: spacing(3),
              }}
            >
              {publishedSections.map((section) => (
                <DSBox
                  key={section.id}
                  sx={{
                    borderRadius: '22px',
                    overflow: 'hidden',
                    backgroundColor: 'paper.elevation2',
                    py: '28px',
                  }}
                >
                  <DSBox
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      px: '36px',
                      pb: '27px',
                    }}
                  >
                    <DSTypography variant="h6" color="common.white">
                      {getSectionTitle(section.type, templates)}
                    </DSTypography>
                  </DSBox>
                  <DSBox
                    sx={{
                      height: '1px',
                      backgroundColor: alpha(palette.common.white, 0.12),
                      width: '100%',
                    }}
                  />
                  <DSBox sx={{ px: '33px', pt: '27px' }}>
                    <SectionTemplateDispatcher
                      section={section}
                      onUpdate={() => undefined}
                      templates={templates}
                      readOnly
                    />
                  </DSBox>
                </DSBox>
              ))}
            </DSBox>
          ) : (
            <EmptySectionsPlaceholder
              onAction={() => {
                handleEnterEditMode();
                setIsAddSectionOpen(true);
              }}
            />
          )}
        </DSBox>
      )}

      {/* ── EDIT MODE ── */}
      {isEditMode && (
        <DSBox sx={containerSx}>
          {/* Header */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xxs: 'flex-start', md: 'center' },
              gap: spacing(2),
              py: spacing(3),
            }}
          >
            <DSBox
              sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
            >
              <DSIconButton
                onClick={() =>
                  hasPublishedSections ? setMode('view') : onBack()
                }
                size="small"
                sx={{
                  color: alpha(palette.common.white, 0.4),
                  mt: '4px',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 28 }} />
              </DSIconButton>
              <DSBox
                sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
              >
                <DSBox
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <DSTypography variant="h3" color="common.white">
                    Custom Sections
                  </DSTypography>
                  {hasSections && (
                    <DSChip
                      label="Editing"
                      size="small"
                      sx={{
                        color: palette.common.white,
                        backgroundColor: 'secondary.main',
                        fontFamily: 'Gilroy-SemiBold',
                        fontSize: '13px',
                        letterSpacing: '0.16px',
                        lineHeight: '18px',
                        height: '24px',
                        borderRadius: '100px',
                        '& .MuiChip-label': { px: '6px' },
                        '&:hover': { backgroundColor: 'secondary.main' },
                      }}
                    />
                  )}
                </DSBox>
                <DSTypography variant="body1" color="text.secondary">
                  Build your artist page by adding sections. These appear below
                  your identity, collections, and Klydos on the marketplace.
                </DSTypography>
              </DSBox>
            </DSBox>

            <DSBox sx={{ display: 'flex', gap: '13px', flexShrink: 0 }}>
              <DSSecondaryButton
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                disabled={!user?.id}
                onClick={() => {
                  if (!user?.id) return;
                  window.open(
                    `${ARTIST_PROFILE_BASE_URL}?id=${user.id}&mode=draft`,
                    '_blank',
                  );
                }}
                sx={previewButtonSx}
              >
                Preview
              </DSSecondaryButton>
              <DSPrimaryButton
                onClick={handlePublish}
                disabled={isPublishing || hasActiveUploads}
                sx={editButtonSx}
              >
                {isPublishing
                  ? 'Publishing...'
                  : hasActiveUploads
                    ? 'Uploading...'
                    : 'Publish'}
              </DSPrimaryButton>
            </DSBox>
          </DSBox>

          {hasSections ? (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '40px',
                pt: { xxs: '40px', sm: '64px' },
                pb: spacing(3),
              }}
            >
              {displayItems.map((section, displayIndex) => {
                const originalIndex = sections.findIndex(
                  (original) => original.id === section.id,
                );
                return (
                  <DSDraggableCard
                    key={section.id}
                    originalIndex={originalIndex}
                    displayIndex={displayIndex}
                    isDragging={draggedItem(section)}
                    handleDragStart={handleDragStart}
                    handleDragEnd={handleDragEnd}
                    handleDragOver={handleDragOver}
                    handleDragEnter={handleDragEnter}
                  >
                    <SectionEditorCard
                      title={getSectionTitle(section.type, templates)}
                      onDelete={() => removeSection(section.id)}
                      isDragActive={draggedIndex !== null}
                    >
                      <SectionTemplateDispatcher
                        section={section}
                        onUpdate={(data) => updateSectionData(section.id, data)}
                        templates={templates}
                        onUploadStart={uploadCallbacks.onUploadStart}
                        onUploadEnd={uploadCallbacks.onUploadEnd}
                      />
                    </SectionEditorCard>
                  </DSDraggableCard>
                );
              })}

              {/* Add Section button */}
              <DSBox
                onClick={() => setIsAddSectionOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '119px',
                  border: `3px dashed ${palette.white[12]}`,
                  borderRadius: '24px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(palette.common.white, 0.05),
                  },
                }}
              >
                <PlusIcon
                  width={30}
                  height={30}
                  style={{ color: alpha(palette.common.white, 0.5) }}
                />
              </DSBox>
            </DSBox>
          ) : (
            <EmptySectionsPlaceholder
              onAction={() => setIsAddSectionOpen(true)}
            />
          )}

          <AddSectionDialog
            open={isAddSectionOpen}
            onClose={() => setIsAddSectionOpen(false)}
            onSelect={handleSectionSelect}
            templates={templates}
          />
        </DSBox>
      )}
    </>
  );
};

export default CustomSectionsView;
